/**
 * Query, mutation and editor state for the "Number series" settings tab.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { nowPlainDate } from '@insio/dates';
import {
  formatDocumentNumber,
  NumberSeriesPatternIssueCode,
  validateNumberSeriesConfig,
  type NumberSeriesPatternIssue
} from '@insio/shared-types';
import {
  NumberSeriesDocument,
  UpdateNumberSeriesDocument,
  type NumberSeriesFieldsFragment
} from '@/graphql';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';

/** The editable part of a series, while the user is typing; `padding` is kept as typed. */
export interface NumberSeriesDraft {
  prefix: string;
  pattern: string;
  padding: string;
}

/** The padding exactly as typed — never coerced into range, so validation can reject it. */
function paddingOf(draft: NumberSeriesDraft): number {
  const raw = draft.padding.trim();
  return raw === '' ? Number.NaN : Number(raw);
}

export function useNumberSeriesSettings() {
  const t = useFeatureTranslation('systemSettings.numberSeries');
  const { showSuccess, showError } = useToastHelpers();

  const [editing, setEditing] = useState<NumberSeriesFieldsFragment | null>(null);
  const [draft, setDraft] = useState<NumberSeriesDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data, loading, error } = useQuery(NumberSeriesDocument);
  const showSkeleton = useDelayedLoading(loading && !data);

  const [updateNumberSeries] = useMutation(UpdateNumberSeriesDocument, {
    refetchQueries: [{ query: NumberSeriesDocument }],
    awaitRefetchQueries: true
  });

  const series = data?.numberSeries ?? [];

  const configIssues: NumberSeriesPatternIssue[] = useMemo(
    () =>
      draft
        ? validateNumberSeriesConfig({
            pattern: draft.pattern,
            prefix: draft.prefix,
            padding: paddingOf(draft),
            nextValue: editing?.nextValue ?? 1
          })
        : [],
    [draft, editing]
  );

  const hasPaddingIssue = configIssues.some(
    issue => issue.code === NumberSeriesPatternIssueCode.PADDING_OUT_OF_RANGE
  );

  // The same renderer the backend uses for its saved-state `preview`, fed the draft values.
  // A padding that cannot be saved renders nothing rather than a number nobody would get.
  const livePreview = useMemo(() => {
    if (!editing || !draft || hasPaddingIssue) return '';
    return formatDocumentNumber({
      pattern: draft.pattern,
      prefix: draft.prefix,
      year: editing.year,
      month: nowPlainDate().month,
      padding: paddingOf(draft),
      sequence: editing.nextValue
    });
  }, [draft, editing, hasPaddingIssue]);

  const openEditor = (row: NumberSeriesFieldsFragment) => {
    setEditing(row);
    setDraft({
      prefix: row.prefix,
      pattern: row.pattern,
      padding: String(row.padding)
    });
    setSaveError(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setDraft(null);
    setSaveError(null);
  };

  const updateDraft = (patch: Partial<NumberSeriesDraft>) => {
    setDraft(current => (current ? { ...current, ...patch } : current));
  };

  const canSave = draft !== null && configIssues.length === 0;

  const handleSave = async () => {
    if (!editing || !draft || !canSave) return;

    setSaveError(null);
    setIsSaving(true);
    try {
      await updateNumberSeries({
        variables: {
          documentType: editing.documentType,
          year: editing.year,
          input: {
            prefix: draft.prefix,
            pattern: draft.pattern,
            padding: paddingOf(draft)
          }
        },
        // The global default is errorPolicy:'all', which resolves on a backend rejection —
        // override so a refused save reaches the catch instead of a false success.
        errorPolicy: 'none'
      });
      showSuccess(t('success.saved'));
      closeEditor();
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : t('errors.saveError');
      setSaveError(message);
      showError(t('errors.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  return {
    t,
    series,
    loading,
    showSkeleton,
    loadError: error,
    editing,
    draft,
    configIssues,
    livePreview,
    canSave,
    isSaving,
    saveError,
    openEditor,
    closeEditor,
    updateDraft,
    handleSave
  };
}
